'use client';

import { useState, useEffect } from 'react';
import ExpertCard from './ExpertCard';
import FilterBar from './FilterBar';
import ExpertModal from './ExpertModal';

interface Expert {
  id: number;
  name: string;
  title: string;
  industry: string;
  yearsExperience: number;
  image: string;
  alt: string;
  expertise: string[];
  location: string;
  availability: 'Available' | 'Limited' | 'Engaged';
  linkedIn: string;
  contributedUseCases: number;
  partnerships: number;
  bio: string;
  education: string;
  certifications: string[];
  speakingEngagements: string[];
  publications: string[];
  endorsements: Array<{
    name: string;
    title: string;
    quote: string;
  }>;
}

const mockExperts: Expert[] = [
{
  id: 1,
  name: "Dr. Sarah Chen",
  title: "Chief Medical Information Officer",
  industry: "Healthcare",
  yearsExperience: 18,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16e01c15b-1763295653314.png",
  alt: "Professional Asian woman doctor in white coat with stethoscope in modern hospital setting",
  expertise: ["Clinical Workflows", "EHR Systems", "Healthcare Analytics", "Patient Safety", "Regulatory Compliance"],
  location: "Boston, MA",
  availability: "Available",
  linkedIn: "https://linkedin.com/in/sarahchen",
  contributedUseCases: 12,
  partnerships: 5,
  bio: "Dr. Sarah Chen brings 18 years of healthcare technology leadership, having led digital transformation initiatives at three major hospital systems. Her expertise spans clinical workflow optimization, EHR implementation, and healthcare data analytics. She has successfully guided multiple AI integration projects that improved patient outcomes while maintaining strict regulatory compliance. Her deep understanding of both clinical practice and technology architecture makes her invaluable for healthcare AI product development.",
  education: "MD from Harvard Medical School, MS in Health Informatics from Stanford University",
  certifications: [
  "Board Certified in Clinical Informatics",
  "Certified Healthcare CIO (CHCIO)",
  "HIMSS Certified Professional in Healthcare Information and Management Systems"],

  speakingEngagements: [
  "HIMSS Global Health Conference 2025 - Keynote on AI in Clinical Decision Support",
  "Healthcare Information and Management Systems Society Annual Conference 2024",
  "MIT Healthcare Innovation Summit 2024 - Panel on Healthcare Data Privacy"],

  publications: [
  "AI-Driven Clinical Decision Support: Lessons from Five Years of Implementation (JAMA, 2025)",
  "Balancing Innovation and Patient Safety in Healthcare AI (New England Journal of Medicine, 2024)"],

  endorsements: [
  {
    name: "Dr. Michael Roberts",
    title: "Chief Medical Officer, Massachusetts General Hospital",
    quote: "Sarah's ability to bridge clinical practice and technology is unmatched. She understands the real constraints of healthcare delivery and designs solutions that actually work in practice."
  },
  {
    name: "Jennifer Martinez",
    title: "VP of Digital Health, Blue Cross Blue Shield",
    quote: "Working with Sarah on our AI initiatives was transformative. She identified problems we didn't even know we had and guided us to solutions that delivered measurable patient outcomes."
  }]

},
{
  id: 2,
  name: "Marcus Thompson",
  title: "Head of Trading Systems Architecture",
  industry: "Finance",
  yearsExperience: 22,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd81bcbd-1763299899173.png",
  alt: "Professional Black man in navy suit with confident expression in modern office environment",
  expertise: ["Algorithmic Trading", "Risk Management", "Market Microstructure", "Regulatory Compliance", "High-Frequency Trading"],
  location: "New York, NY",
  availability: "Limited",
  linkedIn: "https://linkedin.com/in/marcusthompson",
  contributedUseCases: 8,
  partnerships: 3,
  bio: "Marcus Thompson has spent over two decades architecting trading systems for tier-one investment banks and hedge funds. His expertise in market microstructure, algorithmic trading, and risk management has been instrumental in building systems that process billions in daily transactions. He understands the intricate balance between speed, accuracy, and regulatory compliance that defines modern financial markets. His insights into real-world trading constraints have prevented numerous AI projects from pursuing technically impressive but commercially unviable solutions.",
  education: "PhD in Financial Engineering from Columbia University, BS in Computer Science from MIT",
  certifications: [
  "Chartered Financial Analyst (CFA)",
  "Financial Risk Manager (FRM)",
  "Series 7 and Series 63 Securities Licenses"],

  speakingEngagements: [
  "Quantitative Finance Conference 2025 - AI in Algorithmic Trading",
  "Securities Industry and Financial Markets Association Annual Meeting 2024",
  "MIT Sloan Finance Conference 2024 - Panel on Market Structure Evolution"],

  publications: [
  "Machine Learning in High-Frequency Trading: Opportunities and Pitfalls (Journal of Financial Markets, 2025)",
  "Regulatory Considerations for AI-Driven Trading Systems (Risk Magazine, 2024)"],

  endorsements: [
  {
    name: "David Goldstein",
    title: "Managing Director, Goldman Sachs",
    quote: "Marcus has an encyclopedic knowledge of trading systems and market structure. His ability to identify what will actually work in production environments has saved us from countless expensive mistakes."
  }]

},
{
  id: 3,
  name: "Dr. Priya Patel",
  title: "VP of Manufacturing Operations",
  industry: "Manufacturing",
  yearsExperience: 16,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a819d841-1763295537615.png",
  alt: "Professional Indian woman engineer in safety vest and hard hat in industrial manufacturing facility",
  expertise: ["Supply Chain Optimization", "Quality Control", "Predictive Maintenance", "Lean Manufacturing", "Industry 4.0"],
  location: "Detroit, MI",
  availability: "Available",
  linkedIn: "https://linkedin.com/in/priyapatel",
  contributedUseCases: 15,
  partnerships: 7,
  bio: "Dr. Priya Patel has led manufacturing operations for Fortune 500 companies, implementing Industry 4.0 initiatives that delivered measurable improvements in efficiency and quality. Her expertise spans supply chain optimization, predictive maintenance, and quality control systems. She has successfully integrated AI solutions into production environments where downtime costs millions per hour and quality standards are non-negotiable. Her understanding of manufacturing constraints—from union agreements to equipment limitations—ensures AI solutions are practical and implementable.",
  education: "PhD in Industrial Engineering from Georgia Tech, MS in Operations Research from MIT",
  certifications: [
  "Certified Manufacturing Engineer (CMfgE)",
  "Six Sigma Master Black Belt",
  "Certified Supply Chain Professional (CSCP)"],

  speakingEngagements: [
  "International Manufacturing Technology Show 2025 - Keynote on AI in Smart Manufacturing",
  "Society of Manufacturing Engineers Annual Conference 2024",
  "Industry 4.0 Summit 2024 - Panel on Predictive Maintenance"],

  publications: [
  "AI-Driven Predictive Maintenance: A Practical Implementation Guide (Manufacturing Engineering, 2025)",
  "Supply Chain Resilience Through Machine Learning (Harvard Business Review, 2024)"],

  endorsements: [
  {
    name: "Robert Chen",
    title: "COO, General Motors",
    quote: "Priya's deep understanding of manufacturing operations is remarkable. She knows exactly what will work on a factory floor and what won't, saving us from pursuing solutions that look good on paper but fail in practice."
  },
  {
    name: "Lisa Anderson",
    title: "VP of Operations, Boeing",
    quote: "Working with Priya transformed our approach to predictive maintenance. Her expertise in both manufacturing and AI helped us achieve results we didn't think were possible."
  }]

},
{
  id: 4,
  name: "James Morrison",
  title: "Chief Information Security Officer",
  industry: "Cybersecurity",
  yearsExperience: 20,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_199378758-1763294412991.png",
  alt: "Professional Caucasian man in dark suit with serious expression in modern corporate office",
  expertise: ["Threat Detection", "Security Architecture", "Compliance", "Incident Response", "Zero Trust"],
  location: "San Francisco, CA",
  availability: "Engaged",
  linkedIn: "https://linkedin.com/in/jamesmorrison",
  contributedUseCases: 10,
  partnerships: 4,
  bio: "James Morrison has protected enterprise networks for two decades, leading security operations for organizations handling sensitive data across finance, healthcare, and government sectors. His expertise in threat detection, security architecture, and compliance frameworks has been critical in defending against sophisticated attacks. He understands the real-world constraints of security operations—from alert fatigue to resource limitations—and has guided multiple AI security projects to focus on problems that actually matter. His insights prevent AI solutions from creating new vulnerabilities while solving old ones.",
  education: "MS in Cybersecurity from Carnegie Mellon University, BS in Computer Science from UC Berkeley",
  certifications: [
  "Certified Information Systems Security Professional (CISSP)",
  "Certified Information Security Manager (CISM)",
  "Certified Ethical Hacker (CEH)"],

  speakingEngagements: [
  "RSA Conference 2025 - AI in Threat Detection and Response",
  "Black Hat USA 2024 - Machine Learning for Security Operations",
  "SANS Security Summit 2024 - Panel on Zero Trust Architecture"],

  publications: [
  "Machine Learning in Security Operations: Separating Signal from Noise (IEEE Security & Privacy, 2025)",
  "The CISO's Guide to AI Security Tools (Dark Reading, 2024)"],

  endorsements: [
  {
    name: "Sarah Williams",
    title: "CISO, JPMorgan Chase",
    quote: "James has an exceptional ability to cut through vendor hype and identify what actually works in security operations. His guidance has been invaluable in our AI security initiatives."
  }]

},
{
  id: 5,
  name: "Dr. Elena Rodriguez",
  title: "Director of Retail Analytics",
  industry: "Retail",
  yearsExperience: 14,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1098b3c8f-1763293669401.png",
  alt: "Professional Hispanic woman in business casual attire with warm smile in modern retail office",
  expertise: ["Customer Analytics", "Inventory Optimization", "Pricing Strategy", "Omnichannel Retail", "Demand Forecasting"],
  location: "Seattle, WA",
  availability: "Available",
  linkedIn: "https://linkedin.com/in/elenarodriguez",
  contributedUseCases: 11,
  partnerships: 6,
  bio: "Dr. Elena Rodriguez has transformed retail operations for major chains through data-driven decision making and advanced analytics. Her expertise spans customer behavior analysis, inventory optimization, and pricing strategy across physical and digital channels. She has successfully implemented AI solutions that improved margins while maintaining customer satisfaction—a balance that requires deep understanding of retail economics and consumer psychology. Her insights into seasonal patterns, regional variations, and competitive dynamics ensure AI solutions address real business problems rather than academic exercises.",
  education: "PhD in Marketing Analytics from Northwestern University, MBA from Stanford GSB",
  certifications: [
  "Certified Analytics Professional (CAP)",
  "Google Analytics Individual Qualification",
  "Retail Management Certification from NRF"],

  speakingEngagements: [
  "National Retail Federation Big Show 2025 - AI in Retail Operations",
  "Retail Analytics Summit 2024 - Keynote on Demand Forecasting",
  "MIT Retail Innovation Conference 2024 - Panel on Omnichannel Strategy"],

  publications: [
  "AI-Driven Inventory Optimization: Lessons from the Retail Trenches (Harvard Business Review, 2025)",
  "The Future of Retail Pricing: Machine Learning Approaches (Journal of Retailing, 2024)"],

  endorsements: [
  {
    name: "Michael Chen",
    title: "CEO, Target Corporation",
    quote: "Elena's understanding of retail operations is extraordinary. She knows how to balance the art and science of retail in ways that deliver real business results."
  },
  {
    name: "Amanda Foster",
    title: "Chief Merchandising Officer, Nordstrom",
    quote: "Working with Elena on our analytics initiatives was game-changing. Her ability to translate complex data into actionable retail strategies is unmatched."
  }]

},
{
  id: 6,
  name: "Dr. Raj Krishnan",
  title: "VP of Energy Systems",
  industry: "Energy",
  yearsExperience: 19,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12ea075c2-1763300507102.png",
  alt: "Professional Indian man in business suit with confident expression in modern energy facility",
  expertise: ["Grid Optimization", "Renewable Integration", "Energy Trading", "Demand Response", "Smart Grid"],
  location: "Houston, TX",
  availability: "Limited",
  linkedIn: "https://linkedin.com/in/rajkrishnan",
  contributedUseCases: 9,
  partnerships: 4,
  bio: "Dr. Raj Krishnan has led energy systems optimization for utilities and energy companies, managing the complex transition to renewable energy while maintaining grid reliability. His expertise in grid operations, energy trading, and demand response has been critical in integrating AI solutions that work within the physical and regulatory constraints of energy systems. He understands the real-world challenges of balancing supply and demand in real-time, managing distributed energy resources, and operating within strict reliability standards. His insights prevent AI projects from pursuing solutions that ignore the fundamental physics and economics of energy systems.",
  education: "PhD in Electrical Engineering from Stanford University, MS in Energy Systems from MIT",
  certifications: [
  "Professional Engineer (PE) License",
  "Certified Energy Manager (CEM)",
  "NERC Certified Reliability Operator"],

  speakingEngagements: [
  "IEEE Power & Energy Society General Meeting 2025 - AI in Grid Operations",
  "DistribuTECH Conference 2024 - Machine Learning for Demand Response",
  "Clean Energy Summit 2024 - Panel on Renewable Integration"],

  publications: [
  "Machine Learning for Grid Optimization: Practical Considerations (IEEE Transactions on Power Systems, 2025)",
  "AI in Energy Trading: Opportunities and Challenges (Energy Policy, 2024)"],

  endorsements: [
  {
    name: "Patricia Johnson",
    title: "CEO, Pacific Gas & Electric",
    quote: "Raj's expertise in energy systems is comprehensive and practical. He understands both the technical and business aspects of grid operations in ways that few others do."
  }]

}];


export default function ExpertProfilesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:293:6" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="293" data-component-end-line="303" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-16%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:294:8" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="294" data-component-end-line="302" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
          <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:295:10" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="295" data-component-end-line="295" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-surface%20w-64%22%7D" className="h-8 bg-surface w-64"></div>
          <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:296:10" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="296" data-component-end-line="296" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-24%20bg-surface%22%7D" className="h-24 bg-surface"></div>
          <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:297:10" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="297" data-component-end-line="301" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            {[1, 2, 3].map((i) =>
            <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:299:12" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="299" data-component-end-line="299" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-48%20bg-surface%22%7D" key={i} className="h-48 bg-surface"></div>
            )}
          </div>
        </div>
      </div>);

  }

  const industries = Array.from(new Set(mockExperts.map((expert) => expert.industry)));

  const filteredExperts = mockExperts.filter((expert) => {
    const matchesIndustry = selectedIndustry === 'all' || expert.industry === selectedIndustry;
    const matchesAvailability = selectedAvailability === 'all' || expert.availability === selectedAvailability;
    const matchesSearch = searchQuery === '' ||
    expert.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    expert.expertise.some((skill) => skill.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesIndustry && matchesAvailability && matchesSearch;
  });

  const handleViewProfile = (id: number) => {
    const expert = mockExperts.find((e) => e.id === id);
    if (expert) {
      setSelectedExpert(expert);
    }
  };

  const handleCloseModal = () => {
    setSelectedExpert(null);
  };

  return (
    <>
      <FilterBar data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:332:6" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="332" data-component-end-line="339" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D"
        selectedIndustry={selectedIndustry}
        selectedAvailability={selectedAvailability}
        searchQuery={searchQuery}
        onIndustryChange={setSelectedIndustry}
        onAvailabilityChange={setSelectedAvailability}
        onSearchChange={setSearchQuery}
        industries={industries} />


      <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:342:6" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="342" data-component-end-line="346" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
        <p data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:343:8" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="343" data-component-end-line="345" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Showing%20of%20experts%22%7D" className="font-body text-sm text-muted-foreground">
          Showing {filteredExperts.length} of {mockExperts.length} experts
        </p>
      </div>

      {filteredExperts.length === 0 ?
      <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:349:6" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="349" data-component-end-line="363" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
          <p data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:350:10" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="350" data-component-end-line="352" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-base%20text-muted-foreground%20mb-4%22%2C%22textContent%22%3A%22No%20experts%20found%20matching%20your%20criteria.%22%7D" className="font-body text-base text-muted-foreground mb-4">
            No experts found matching your criteria.
          </p>
          <button data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:353:10" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="353" data-component-end-line="362" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22font-cta%20text-sm%20font-medium%20px-6%20py-2%20bg-primary%20text-primary-foreground%20hover%3Abg-accent%20transition-colors%20duration-200%20border%20border-primary%22%2C%22textContent%22%3A%22Clear%20Filters%22%7D"
          onClick={() => {
            setSelectedIndustry('all');
            setSelectedAvailability('all');
            setSearchQuery('');
          }}
          className="font-cta text-sm font-medium px-6 py-2 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-200 border border-primary">

            Clear Filters
          </button>
        </div> :

      <div data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:365:6" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="365" data-component-end-line="373" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
          {filteredExperts.map((expert) =>
        <ExpertCard data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:367:8" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="367" data-component-end-line="370" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="ExpertCard" data-component-content="%7B%22elementName%22%3A%22ExpertCard%22%7D"
          key={expert.id}
          expert={expert}
          onViewProfile={handleViewProfile} />

        )}
        </div>
      }

      <ExpertModal data-component-id="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx:376:6" data-component-path="src/app/expert-profiles/components/ExpertProfilesInteractive.tsx" data-component-line="376" data-component-end-line="376" data-component-file="ExpertProfilesInteractive.tsx" data-component-name="ExpertModal" data-component-content="%7B%22elementName%22%3A%22ExpertModal%22%7D" expert={selectedExpert} onClose={handleCloseModal} />
    </>);

}